import React, { useState, useEffect, useRef } from "react";
import { Row, Container, Button, Col, Modal, Form, InputGroup, Offcanvas, FloatingLabel } from "react-bootstrap";
import { FaPlay, FaPause, FaCog, FaBell, FaBars, FaUndo, FaBellSlash } from "react-icons/fa";
import { Link } from "react-router-dom";
import { toast } from 'react-toastify';

function formatTime(seconds) {
  if (seconds < 0) seconds = 0;
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0");
  const s = Math.floor(seconds % 60)
    .toString()
    .padStart(2, "0");
  return `${m}:${s}`;
}

const Timer = () => {
  const defaultTotal = 20 * 60; // 20 minutes
  const [total, setTotal] = useState(defaultTotal);
  const [remaining, setRemaining] = useState(defaultTotal);
  const [running, setRunning] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [warn1, setWarn1] = useState(5 * 60); // orange at 5 min
  const [warn2, setWarn2] = useState(2 * 60); // red at 2 min
  const [alarms, setAlarms] = useState([]); // in seconds remaining
  const [newAlarm, setNewAlarm] = useState(60 * 10); // default 10m
  const [alarmMinutes, setAlarmMinutes] = useState(10);
  const [alarmSeconds, setAlarmSeconds] = useState(0);
  const [enableEndAlarm, setEnableEndAlarm] = useState(true); // enable alarm when timer reaches 0

  const intervalRef = useRef(null);
  const flashRef = useRef(null);
  const [flashOn, setFlashOn] = useState(false);

  useEffect(() => {
    if (running) {
      intervalRef.current = setInterval(() => {
        setRemaining((r) => r - 1);
      }, 1000);
    }
    return () => clearInterval(intervalRef.current);
  }, [running]);

  useEffect(() => {
    // Trigger alarms when crossing times
    alarms.forEach((a) => {
      if (remaining === a) {
        playBeep();
        toast.info(`Alarm: ${formatTime(a)} remaining`);
      }
    });

    if (remaining <= 0) {
      setRunning(false);
      startFlashing();
      if (enableEndAlarm) {
        playBeep(3);
      }
    }

    return () => { };
  }, [remaining]);

  function startFlashing() {
    if (flashRef.current) return;
    flashRef.current = setInterval(() => {
      setFlashOn((v) => !v);
    }, 500);
  }

  function stopFlashing() {
    clearInterval(flashRef.current);
    flashRef.current = null;
    setFlashOn(false);
  }

  useEffect(() => {
    if (running) stopFlashing();
  }, [running]);

  function playBeep(repeat = 1) {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      for (let i = 0; i < repeat; i++) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.frequency.value = 880;
        osc.type = "sine";
        osc.connect(gain);
        gain.connect(ctx.destination);
        gain.gain.value = 0.05;
        const start = ctx.currentTime + i * 0.25;
        osc.start(start);
        osc.stop(start + 0.12);
      }
    } catch (e) {
      // fallback: console
      console.log("beep");
    }
  }

  const color = remaining <= 0 ? "#ff0000" : remaining <= warn2 ? "#ff3b3b" : remaining <= warn1 ? "#ff9f1c" : "#4caf50";

  function handleStartPause() {
    if (running) {
      setRunning(false);
    } else {
      if (remaining <= 0) setRemaining(total);
      setRunning(true);
    }
  }

  function handleToggleAlarm() {
    setEnableEndAlarm((prev) => !prev);
  }

  function handleReset() {
    setRunning(false);
    stopFlashing();
    setRemaining(total);
  }

  function handleOpenSettings() {
    setShowSettings(true);
  }

  function handleToggleMenu() {
    setShowMenu((s) => !s);
  }

  function handleSaveSettings(e) {
    e.preventDefault();
    const form = e.target;
    const minutes = parseInt(form.totalMinutes.value || 0, 10);
    const seconds = parseInt(form.totalSeconds.value || 0, 10);
    const t = minutes * 60 + seconds;
    if (t > 0) {
      setTotal(t);
      setRemaining(t);
    }
    setWarn1((parseInt(form.warn1m.value || 0, 10) * 60) + parseInt(form.warn1s.value || 0, 10));
    setWarn2((parseInt(form.warn2m.value || 0, 10) * 60) + parseInt(form.warn2s.value || 0, 10));
    setEnableEndAlarm(form.enableEndAlarm.checked);
    setShowSettings(false);
  }

  function addAlarm(e) {
    e.preventDefault();
    const a = alarmMinutes * 60 + alarmSeconds;
    if (!Number.isFinite(a) || a <= 0 || a > total) return;
    if (!alarms.includes(a)) setAlarms((s) => [...s, a].sort((x, y) => y - x));
    setAlarmMinutes(5);
    setAlarmSeconds(0);
    toast.success(`Alarm added at ${formatTime(a)} remaining`);
  }

  function removeAlarm(a) {
    setAlarms((s) => s.filter((x) => x !== a));
  }

  return (
    <Container fluid style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', padding: 0, border: `50px solid ${color}`, boxSizing: 'border-box', backgroundColor: '#000000', transition: 'border-color 0.4s' }}>
      <Button variant="link" onClick={handleToggleMenu} style={{ position: 'absolute', top: 18, left: 18, zIndex: 1000, color: '#fff' }} aria-label="Open menu"><FaBars size={28} /></Button>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', flexDirection: 'column' }}>
        <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
          <span style={{ display: 'inline-block', textAlign: 'center', fontSize: 'clamp(4rem, 25vw, 50vh)', fontWeight: 800, lineHeight: 1, color: remaining <= 0 && flashOn ? '#ffffff' : color, transition: 'color 0.4s', fontFamily: "'Space Grotesk', 'Space Mono', monospace", fontVariantNumeric: 'tabular-nums', whiteSpace: 'pre' }}>{formatTime(remaining)}</span>
        </div>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <Button onClick={handleStartPause} style={{ fontSize: '1.5rem', padding: '0.5rem 0.75rem', backgroundColor: 'transparent', borderColor: '#666', color: '#666', fontFamily: "'Courier New', 'Courier', monospace" }} variant="outline-secondary">
            {running ? <FaPause /> : <FaPlay />}
          </Button>
          <Button onClick={handleToggleAlarm} style={{ fontSize: '1.5rem', padding: '0.5rem 0.75rem', backgroundColor: 'transparent', borderColor: enableEndAlarm ? '#666' : '#444', color: enableEndAlarm ? '#666' : '#444', fontFamily: "'Courier New', 'Courier', monospace", opacity: enableEndAlarm ? 1 : 0.5 }} variant="outline-secondary">
            {enableEndAlarm ? <FaBell /> : <FaBellSlash />}
          </Button>
        </div>
      </div>

      <Offcanvas show={showMenu} onHide={() => setShowMenu(false)} placement="start" style={{ backgroundColor: '#000000' }}>
        <Offcanvas.Header closeButton style={{ backgroundColor: '#000000', borderBottom: '1px solid #333' }}>
          <Offcanvas.Title style={{ color: '#fff' }}>Timer Controls</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body style={{ backgroundColor: '#000000', color: '#fff' }}>
          <div className="mb-2">
            <Button variant="primary" onClick={() => { handleStartPause(); setShowMenu(false); }} className="me-2">
              {running ? <FaPause /> : <FaPlay />} {running ? 'Pause' : 'Start'}
            </Button>
            <Button variant="secondary" onClick={() => { handleReset(); setShowMenu(false); }} className="me-2"><FaUndo /> Reset</Button>
            <Button variant="outline-secondary" onClick={() => { handleOpenSettings(); setShowMenu(false); }}><FaCog /> Settings</Button>
          </div>

          <hr />

          <h6>Alarms</h6>
          <Form onSubmit={(e) => { addAlarm(e); setShowMenu(true); }} className="d-flex gap-2 mb-2">
            <InputGroup style={{ width: 200 }}>
              <Form.Control type="number" min={0} placeholder="Min" value={alarmMinutes} onChange={(e) => setAlarmMinutes(Number(e.target.value) || 0)} />
              <Form.Control type="number" min={0} max={59} placeholder="Sec" value={alarmSeconds} onChange={(e) => setAlarmSeconds(Number(e.target.value) || 0)} />
              <Button variant="outline-primary" type="submit"><FaBell /></Button>
            </InputGroup>
          </Form>
          <div>
            {alarms.length === 0 && <div className="text-muted">No alarms</div>}
            {alarms.map((a) => (
              <div key={a} className="d-flex justify-content-between align-items-center border p-1 mb-1">
                <div>{formatTime(a)}</div>
                <Button size="sm" variant="outline-danger" onClick={() => removeAlarm(a)}>Remove</Button>
              </div>
            ))}
          </div>

          <hr />
          <div className="mt-2">
            <Link to="/"><Button variant="outline-secondary">Return to Improviser</Button></Link>
          </div>
          <div className="mt-2">
            <form action="https://www.paypal.com/donate" method="post" target="_top">
              <input type="hidden" name="hosted_button_id" value="BW8ZC3ZHMMA4A" />
              <a href="https://www.paypal.com/donate" target="_blank" rel="noreferrer">
                <input type="image" src="https://www.paypalobjects.com/en_US/GB/i/btn/btn_donateCC_LG.gif" border="0" name="submit" title="PayPal - The safer, easier way to pay online!" alt="Donate with PayPal button" />
              </a>
              <img alt="" border="0" src="https://www.paypal.com/en_GB/i/scr/pixel.gif" width="1" height="1" />
            </form>
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      <Modal show={showSettings} onHide={() => setShowSettings(false)}>
        <Form onSubmit={handleSaveSettings}>
          <Modal.Header closeButton>
            <Modal.Title>Timer Settings</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-2">
              <Form.Label>Total time</Form.Label>
              <div className="d-flex gap-2">
                <InputGroup className="mb-3">
                  <InputGroup.Text id="totalMinutes">Minutes</InputGroup.Text>
                  <Form.Control name="totalMinutes" type="number" defaultValue={Math.floor(total / 60)} min={0} />
                </InputGroup>
                <InputGroup className="mb-3">
                  <InputGroup.Text id="totalSeconds">Seconds</InputGroup.Text>

                  <Form.Control name="totalSeconds" type="number" defaultValue={total % 60} min={0} max={59} />
                </InputGroup>
              </div>
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Warning 1 (Orange)</Form.Label>
              <div className="d-flex gap-2">
                <InputGroup className="mb-3">
                  <InputGroup.Text id="warn1Minutes">Minutes</InputGroup.Text>
                  <Form.Control name="warn1m" type="number" defaultValue={Math.floor(warn1 / 60)} min={0} />
                </InputGroup>
                <InputGroup className="mb-3">
                  <InputGroup.Text id="warn1Seconds">Seconds</InputGroup.Text>
                  <Form.Control name="warn1s" type="number" defaultValue={warn1 % 60} min={0} max={59} />
                </InputGroup>
              </div>
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Warning 2 (Red)</Form.Label>
              <div className="d-flex gap-2">
                <InputGroup className="mb-3">
                  <InputGroup.Text id="warn2Minutes">Minutes</InputGroup.Text>
                  <Form.Control name="warn2m" type="number" defaultValue={Math.floor(warn2 / 60)} min={0} />
                </InputGroup>
                <InputGroup className="mb-3">
                  <InputGroup.Text id="warn2Seconds">Seconds</InputGroup.Text>
                  <Form.Control name="warn2s" type="number" defaultValue={warn2 % 60} min={0} max={59} />
                </InputGroup>
              </div>
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Check name="enableEndAlarm" type="checkbox" defaultChecked={enableEndAlarm} label="Sound alarm when timer reaches 0" />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowSettings(false)}>Cancel</Button>
            <Button variant="primary" type="submit">Save</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default Timer;
